import React, { useState } from 'react';
import * as AlertDialog from '@radix-ui/react-alert-dialog';
import { PlusIcon, TrashIcon } from '@heroicons/react/outline';
import AddFieldForm from './AddFieldForm';

export default function PatientSetupFields() {
  const [fields, setFields] = useState([]);
  const [showAddDialog, setShowAddDialog] = useState(false);

  const handleAddField = (newField) => {
    setFields(prev => [...prev, { id: Date.now(), ...newField }]);
    setShowAddDialog(false);
  };

  const handleDeleteField = (fieldId) => {
    setFields(prev => prev.filter(field => field.id !== fieldId));
  };

  return (
    <div className="max-w-7xl mx-auto">
      <div className="space-y-4">
        {/* Header */}
        <div>
          <h2 className="text-lg font-medium text-gray-900">Patient Setup Fields</h2>
          <p className="mt-1 text-sm text-gray-500">
            Configure the fields that will be required when setting up a new patient.
          </p>
        </div>

        {/* Fields Table */}
        <div className="bg-gray-50 border border-gray-200 rounded-xl shadow-sm overflow-hidden">
          <div className="min-w-full">
            {fields.length > 0 ? (
              <table className="min-w-full divide-y divide-gray-200">
                <thead>
                  <tr className="bg-gray-50">
                    <th scope="col" className="py-4 pl-6 pr-3 text-xs font-medium text-gray-800 tracking-wider">
                      Field Name
                    </th>
                    <th scope="col" className="px-3 py-4 text-xs font-medium text-gray-800 tracking-wider">
                      Type
                    </th>
                    <th scope="col" className="px-3 py-4 text-xs font-medium text-gray-800 tracking-wider">
                      Options
                    </th>
                    <th scope="col" className="py-4 pl-3 pr-6 text-right">
                      <span className="sr-only">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {fields.map((field, index) => (
                    <tr 
                      key={field.id}
                      className="hover:bg-gray-50 transition-colors duration-150 ease-in-out"
                    >
                      <td className="py-4 pl-6 pr-3 text-sm">
                        <div className="font-medium text-gray-900">{field.name}</div>
                      </td>
                      <td className="px-3 py-4 text-sm text-gray-500">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize bg-gray-100 text-gray-800">
                          {field.type}
                        </span>
                      </td>
                      <td className="px-3 py-4 text-sm text-gray-500">
                        {field.options ? (
                          <div className="flex flex-wrap gap-1">
                            {field.options.map((option, i) => (
                              <span 
                                key={i}
                                className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-violet-50 text-violet-700"
                              >
                                {option}
                              </span>
                            ))}
                          </div>
                        ) : "—"}
                      </td>
                      <td className="py-4 pl-3 pr-6 text-right text-sm">
                        <button
                          onClick={() => handleDeleteField(field.id)}
                          className="text-gray-400 hover:text-red-600 transition-colors duration-150"
                        >
                          <TrashIcon className="h-5 w-5" />
                          <span className="sr-only">Delete</span>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="text-center py-6">
                <h3 className="text-sm font-medium text-gray-900">No fields added</h3>
                <p className="mt-1 text-sm text-gray-500">Get started by adding your first field.</p>
              </div>
            )}
          </div>
        </div>

        {/* Add Field Button */}
        <div className="flex justify-end">
          <button
            onClick={() => setShowAddDialog(true)}
            className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-violet-600 hover:bg-violet-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-violet-500"
          >
            <PlusIcon className="-ml-1 mr-2 h-5 w-5" />
            Add Field
          </button>
        </div>

        {/* Add Field Dialog */}
        <AlertDialog.Root open={showAddDialog} onOpenChange={setShowAddDialog}>
          <AlertDialog.Portal>
            <AlertDialog.Overlay className="radix-dialog-overlay-motion fixed inset-0 bg-black/50" />
            <AlertDialog.Content className="radix-dialog-content-centered-motion fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[95vw] max-w-md rounded-lg bg-white p-6 shadow-xl focus:outline-none">
              <AlertDialog.Title className="text-lg font-medium text-gray-900 mb-4">
                Add New Field
              </AlertDialog.Title>
              <AddFieldForm onSubmit={handleAddField} onCancel={() => setShowAddDialog(false)} />
            </AlertDialog.Content>
          </AlertDialog.Portal>
        </AlertDialog.Root>
      </div>
    </div>
  );
}